import { useEffect, useState } from "react";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
const signed = (n) => `${n > 0 ? "+" : ""}${money(n)}`;

/** Side by side: the PO's lines on the left, what the vendor quoted on the right. Only the
 *  ticked rows are written, and only onto a PREPARED PO. */
export default function QuoteMergePanel({ project, po, quote, canEdit, onBack, onApplied }) {
  const [cmp, setCmp] = useState(null);
  const [ticked, setTicked] = useState({});
  const [extras, setExtras] = useState({});
  const [freight, setFreight] = useState(false);
  const [lead, setLead] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    opsApi.poQuoteCompare(project.id, po.id, quote.id).then((c) => {
      setCmp(c);
      const t = {};
      c.rows.forEach((r) => { if (r.po_line_id && !r.no_price_on_quote && r.unit_delta !== 0) t[r.po_line_id] = true; });
      setTicked(t);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not compare"));
  }, [project.id, po.id, quote.id]);

  const apply = async () => {
    const lines = cmp.rows.filter((r) => r.po_line_id && ticked[r.po_line_id])
      .map((r) => ({ po_line_id: r.po_line_id, unit_cost: r.quote_unit_price }));
    const add_lines = Object.keys(extras).filter((k) => extras[k]).map((k) => ({ quote_index: Number(k) }));
    if (!lines.length && !add_lines.length && !freight && !lead) {
      toast.error("Tick at least one line, the freight or the lead time");
      return;
    }
    setBusy(true);
    try {
      const res = await opsApi.poQuoteApply(project.id, po.id, quote.id, { lines, add_lines,
        apply_freight: freight, apply_lead_time: lead });
      toast.success(`${res.applied.lines.length} line(s) applied from ${quote.quote_ref || "the quote"} to ${po.po_number}`);
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be applied"); }
    finally { setBusy(false); }
  };

  const rows = cmp?.rows.filter((r) => r.po_line_id) || [];
  const extraRows = cmp?.rows.filter((r) => r.extra) || [];
  const canApply = canEdit && cmp?.can_apply;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onBack}>
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-po-quote-merge">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="text-slate-500 hover:text-slate-800" data-testid="ops-po-quote-merge-back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h3 className="text-base font-semibold text-slate-900">
            {po.po_number} vs quote {quote.quote_ref || "(no #)"} · {quote.vendor || quote.manufacturer}
          </h3>
        </div>
        {!cmp ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Comparing…</p>
        ) : (
          <>
            <div className="mt-3 grid gap-2 text-xs sm:grid-cols-4" data-testid="ops-po-quote-summary">
              <Stat label="PO total (lines)" value={money(cmp.summary.po_total)} />
              <Stat label="At quoted prices" value={money(cmp.summary.quoted_total)}
                    sub={<span className={cmp.summary.delta > 0 ? "text-rose-700" : "text-emerald-700"}>{signed(cmp.summary.delta)}</span>} />
              <Stat label="Matched" value={`${cmp.summary.matched} of ${cmp.summary.po_lines}`}
                    sub={cmp.summary.no_price_on_quote ? `${cmp.summary.no_price_on_quote} not priced on the quote` : "every PO line priced"} />
              <Stat label="Valid through" value={quote.valid_through || "—"}
                    sub={cmp.summary.expired ? <span className="text-amber-700">expired</span> : `${cmp.summary.extras_on_quote} extra row(s) on the quote`} />
            </div>
            {cmp.why_not && (
              <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="ops-po-quote-why-not">{cmp.why_not}</p>
            )}

            <table className="mt-3 w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1.5"></th>
                  <th className="px-2 py-1.5 text-left" colSpan={2}>Purchase order</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Unit</th>
                  <th className="border-l border-slate-200 px-2 py-1.5 text-left" colSpan={2}>Quote</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Unit</th>
                  <th className="px-2 py-1.5 text-right">Δ unit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.po_line_id} data-testid={`ops-po-quote-row-${r.po_line_id}`}
                      className={r.no_price_on_quote ? "text-slate-400" : ""}>
                    <td className="px-2 py-1">
                      {!r.no_price_on_quote && (
                        <input type="checkbox" checked={!!ticked[r.po_line_id]} disabled={!canApply}
                               onChange={(e) => setTicked((t) => ({ ...t, [r.po_line_id]: e.target.checked }))}
                               data-testid={`ops-po-quote-tick-${r.po_line_id}`} />
                      )}
                    </td>
                    <td className="px-2 py-1 font-mono">{r.item_number || "—"}</td>
                    <td className="px-2 py-1">{r.description}<span className="ml-1 text-slate-400">{r.cost_code}</span></td>
                    <td className="px-2 py-1 text-right">{r.po_qty}</td>
                    <td className="px-2 py-1 text-right">{money(r.po_unit_cost)}</td>
                    <td className="border-l border-slate-200 px-2 py-1 font-mono">{r.no_price_on_quote ? "" : (r.quote_item_number || "—")}</td>
                    <td className="px-2 py-1">
                      {r.no_price_on_quote ? <i>not on this quote</i> : r.quote_description}
                      {!r.no_price_on_quote && <span className="ml-1 text-[10px] text-slate-400">by {r.matched_by}</span>}
                    </td>
                    <td className={`px-2 py-1 text-right ${r.qty_differs ? "font-semibold text-amber-700" : ""}`}>
                      {r.no_price_on_quote ? "" : r.quote_qty}
                    </td>
                    <td className="px-2 py-1 text-right">{r.no_price_on_quote ? "" : money(r.quote_unit_price)}</td>
                    <td className={`px-2 py-1 text-right ${r.unit_delta > 0 ? "text-rose-700" : r.unit_delta < 0 ? "text-emerald-700" : ""}`}>
                      {r.no_price_on_quote ? "" : signed(r.unit_delta)}
                    </td>
                  </tr>
                ))}
                {extraRows.map((r) => (
                  <tr key={`x-${r.quote_index}`} className="bg-sky-50/40" data-testid={`ops-po-quote-extra-${r.quote_index}`}>
                    <td className="px-2 py-1">
                      <input type="checkbox" checked={!!extras[r.quote_index]} disabled={!canApply}
                             onChange={(e) => setExtras((x) => ({ ...x, [r.quote_index]: e.target.checked }))}
                             data-testid={`ops-po-quote-extra-tick-${r.quote_index}`} />
                    </td>
                    <td className="px-2 py-1 text-slate-400" colSpan={4}><i>not on the PO — tick to add it</i></td>
                    <td className="border-l border-slate-200 px-2 py-1 font-mono">{r.item_number || "—"}</td>
                    <td className="px-2 py-1">{r.description}</td>
                    <td className="px-2 py-1 text-right">{r.qty}</td>
                    <td className="px-2 py-1 text-right">{money(r.unit_price)}</td>
                    <td className="px-2 py-1 text-right">{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={freight} disabled={!canApply} onChange={(e) => setFreight(e.target.checked)}
                       data-testid="ops-po-quote-freight" />
                Freight from the quote: {quote.freight ? money(quote.freight) : "prepaid / none"}
                <span className="text-slate-400">(PO: {cmp.po.freight_mode === "amount" ? money(cmp.po.freight_amount) : "prepaid"})</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={lead} disabled={!canApply || cmp.summary.lead_time_days_on_quote === null}
                       onChange={(e) => setLead(e.target.checked)} data-testid="ops-po-quote-lead" />
                Lead time from the quote: {quote.lead_time || "not read"}
                {cmp.summary.lead_time_days_on_quote !== null && <span className="text-slate-400">(= {cmp.summary.lead_time_days_on_quote} days; PO: {cmp.po.lead_time_days})</span>}
              </label>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onBack} data-testid="ops-po-quote-merge-cancel">Back</Button>
              {canApply && (
                <Button size="sm" onClick={apply} disabled={busy} data-testid="ops-po-quote-apply">
                  {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Apply the ticked prices to {po.po_number}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 p-2.5">
      <p className="text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}
